<template>
  <div @click="openOtherScreen" class="customer-screen-container">
    <!-- Welcome Screen (Basket is empty) -->
    <div v-if="!cartEntries || cartEntries.length === 0" class="welcome-screen">
      <div class="welcome-content">
        <div class="logo-wrapper">
          <img :src="branchPhotoUrl" class="brand-logo" alt="BrewMood Logo">
        </div>
        <h1 class="welcome-title">Hoş Geldiniz</h1>
        <p class="welcome-subtitle" v-if="branchName">{{ branchName }}</p>
      </div>
    </div>

    <!-- Active Cart Screen -->
    <div v-else class="cart-screen-layout">
      <!-- Left: Products List -->
      <div class="products-section">
        <div class="section-header">
          <span class="header-title">Sipariş Detayı</span>
          <span class="items-count">{{ totalItemsCount }} Ürün</span>
        </div>
        
        <div class="products-list-wrapper" ref="productsList">
          <transition-group name="list" tag="div" class="products-list">
            <div 
              v-for="(item, index) in cartEntries" 
              :key="'item-' + (item.id || index)" 
              class="product-row-card"
              :class="{ 'new-item-added': index === cartEntries.length - 1 && shouldBlink }"
            >
              <div class="product-info-col">
                <div class="product-name-row">
                  <span class="product-name">{{ item.productName }}</span>
                  <span class="product-quantity-badge">x{{ item.quantity }}</span>
                </div>
                
                <!-- Extras & Modifications -->
                <div class="product-extras-wrapper" v-if="hasExtrasOrRecipe(item)">
                  <!-- Extras -->
                  <div class="extra-item" v-for="(extra, eindex) in item.extras" :key="'extra-'+eindex">
                    <span v-if="extra.quantity > 0 && extra.variants.length === 0">
                      + {{ extra.name }} (x{{ extra.quantity }})
                    </span>
                    <span v-else-if="extra.variants.length > 0" v-for="vExtra in returnSelectedExtraVariant(extra)" :key="vExtra">
                      + {{ vExtra }}
                    </span>
                  </div>
                  <!-- Recipe Steps -->
                  <div class="extra-item" v-for="(step, rindex) in item.recipe.steps" :key="'step'+rindex">
                    <span v-if="step.originRawMaterialId != null && step.selected && !step.isRemoved">
                      ✓ {{ step.mobileName || step.name }}
                    </span>
                  </div>
                </div>
              </div>
              
              <div class="product-price-col">
                <span class="product-price">{{ $toCurrency(item.price) }}</span>
              </div>
            </div>
            
            <!-- Discounts -->
            <div 
              v-for="(discount, dindex) in discounts" 
              :key="'discount-' + dindex" 
              class="discount-row-card"
            >
              <div class="discount-info-col">
                <span class="discount-label-badge">İNDİRİM</span>
                <span class="discount-desc">{{ discount.description }}</span>
              </div>
              <div class="discount-price-col">
                <span class="discount-amount">-{{ $toCurrency(discount.discountAmount || discount.value) }}</span>
              </div>
            </div>
          </transition-group>
        </div>
      </div>

      <!-- Right: Summary Panel -->
      <div class="summary-section">
        <div class="brand-panel">
          <img :src="branchPhotoUrl" class="brand-logo-small" alt="BrewMood Logo">
          <div class="brand-text">{{ branchName }}</div>
        </div>
        
        <div class="summary-card">
          <div class="summary-title">Ödeme Özeti</div>
          
          <div class="summary-row" v-if="totalDiscountAmount > 0">
            <span class="summary-label">Ara Toplam</span>
            <span class="summary-value">{{ $toCurrency(totalOriginPrice) }}</span>
          </div>
          
          <div class="summary-row discount-row" v-if="totalDiscountAmount > 0">
            <span class="summary-label">Toplam İndirim</span>
            <span class="summary-value">-{{ $toCurrency(totalDiscountAmount) }}</span>
          </div>
          
          <div class="summary-divider"></div>
          
          <div class="total-row">
            <span class="total-label">Ödenecek Tutar</span>
            <div class="total-price-value">
              {{ $toCurrency(totalPrice) }}
            </div>
          </div>
        </div>
        
        
      </div>
    </div>
  </div>
</template>

<script>
import { currentBranch } from '@/api/Branch.js'

export default {
  name: 'CustomerScreen',

  data() {
    return {
      cartEntries: [],
      discounts: [],
      broadcaster: null,
      shouldBlink: false,
      branchPhotoUrl: '/img/pickposlogo.jpg', // fallback image
      branchName: ''
    }
  },

  computed: {
    totalPrice() {
      const itemsTotal = this.cartEntries.reduce((sum, item) => sum + item.price, 0);
      const discountTotal = this.discounts.reduce((sum, discount) => sum + (discount.discountAmount || 0), 0);
      return Math.max(0, itemsTotal - discountTotal);
    },
    totalOriginPrice() {
      return this.cartEntries.reduce((sum, item) => sum + item.price, 0);
    },
    totalDiscountAmount() {
      return this.discounts.reduce((sum, discount) => sum + (discount.discountAmount || discount.value || 0), 0);
    },
    totalItemsCount() {
      return this.cartEntries.reduce((sum, item) => sum + item.quantity, 0);
    }
  },

  async created() {
    document.title = 'Siparişiniz Hazırlanıyor...'

    // Fetch branch data for photo and name
    try {
      const branchResponse = await currentBranch();
      if (branchResponse.data) {
        if (branchResponse.data.photo) {
          this.branchPhotoUrl = `${process.env.VUE_APP_ROOT_API}${branchResponse.data.photo}`;
        }
        if (branchResponse.data.name) {
          this.branchName = branchResponse.data.name;
        }
      }
    } catch (error) {
      console.error('Error fetching branch data:', error);
    }

    // Broadcast kanalına bağlan
    this.broadcaster = new BroadcastChannel('cart-channel');

    // Mesajları dinle
    this.broadcaster.onmessage = (event) => {
      if (event.data.type === 'CART_UPDATE') {
        if (Array.isArray(event.data.data)) {
          this.cartEntries = event.data.data;
          this.discounts = [];
        } else if (event.data.data && typeof event.data.data === 'object') {
          this.cartEntries = event.data.data.cartEntries || [];
          this.discounts = event.data.data.discounts || [];
        }
        this.scrollToBottom();
        this.triggerBlink();
      }
    };
  },

  beforeDestroy() {
    if (this.broadcaster) {
      this.broadcaster.close();
    }
  },

  methods: {
    async openOtherScreen() {
      try {
        if ('getScreenDetails' in window) {
          var screenResult = (await window.getScreenDetails());
          console.log(screenResult);
          document.body.requestFullscreen({screen:screenResult.screens.find(screen => !screen.isPrimary)});
        } else {
          console.warn('getScreenDetails API is not supported in this browser');
          document.body.requestFullscreen();
        }
      } catch (error) {
        console.error('Error accessing screen details:', error);
        try {
          document.body.requestFullscreen();
        } catch (fallbackError) {
          console.error('Fullscreen not supported:', fallbackError);
        }
      }
    },
    returnSelectedExtraVariant(extra) {
      let returnObj = [];
      extra.variants.filter(x => {
        if (x.quantity > 0) {
          returnObj.push("x" + x.quantity + " | " + x.name)
        }
      });
      return returnObj.length > 0 ? returnObj : null;
    },
    hasExtrasOrRecipe(item) {
      const hasExtras = item.extras && item.extras.some(e => e.quantity > 0);
      const hasRecipeSteps = item.recipe && item.recipe.steps && item.recipe.steps.some(s => s.originRawMaterialId != null && s.selected && !s.isRemoved);
      return hasExtras || hasRecipeSteps;
    },
    triggerBlink() {
      this.shouldBlink = true;
      setTimeout(() => {
        this.shouldBlink = false;
      }, 600); // 600ms to allow smooth pulse fadeout
    },
    scrollToBottom() {
      setTimeout(() => {
        const container = this.$refs.productsList;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

/* Style Reset & Fonts */
.customer-screen-container {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: #0c0807;
  color: #f7ebe6;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
}

/* Hide scrollbars */
.customer-screen-container, 
.products-list-wrapper {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.customer-screen-container::-webkit-scrollbar,
.products-list-wrapper::-webkit-scrollbar {
  display: none;
}

/* Welcome Screen */
.welcome-screen {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 50% 50%, #201311 0%, #0c0807 100%);
  min-height: 100vh;
  padding: 40px;
  text-align: center;
}

.welcome-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 600px;
}

.logo-wrapper {
  margin-bottom: 30px;
  position: relative;
}

.brand-logo {
  max-width: 420px;
  height: auto;
  border-radius: 60px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(212, 163, 115, 0.2);
  animation: logoPulse 4s infinite ease-in-out;
}

@keyframes logoPulse {
  0% { transform: scale(1); box-shadow: 0 15px 35px rgba(0, 0, 0, 0.6); }
  50% { transform: scale(1.03); box-shadow: 0 20px 40px rgba(212, 163, 115, 0.15); }
  100% { transform: scale(1); box-shadow: 0 15px 35px rgba(0, 0, 0, 0.6); }
}

.welcome-title {
  font-size: 3rem;
  font-weight: 800;
  color: #e6c594;
  margin: 0 0 12px 0;
  letter-spacing: -0.02em;
}

.welcome-subtitle {
  font-size: 1.3rem;
  color: #cca495;
  margin: 0;
  font-weight: 500;
}

/* Cart Screen Layout */
.cart-screen-layout {
  display: flex;
  flex: 1;
  min-height: 100vh;
  overflow: hidden;
}

/* Left Panel: Products */
.products-section {
  flex: 6;
  display: flex;
  flex-direction: column;
  padding: 40px;
  background-color: #0c0807;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(212, 163, 115, 0.15);
  padding-bottom: 16px;
}

.header-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: #f7ebe6;
  letter-spacing: -0.01em;
}

.items-count {
  font-size: 1.1rem;
  background: rgba(212, 163, 115, 0.12);
  color: #e6c594;
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 600;
  border: 1px solid rgba(212, 163, 115, 0.15);
}

.products-list-wrapper {
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;
}

.products-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Product Cards */
.product-row-card {
  background: #150e0c;
  border: 1px solid rgba(255, 255, 255, 0.02);
  border-radius: 20px;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.product-row-card:hover {
  transform: translateY(-2px);
  border-color: rgba(212, 163, 115, 0.1);
}

.product-info-col {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-right: 20px;
}

.product-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #f7ebe6;
  line-height: 1.3;
}

.product-quantity-badge {
  font-size: 1.25rem;
  font-weight: 800;
  color: #e6c594;
  background: rgba(230, 197, 148, 0.08);
  padding: 2px 10px;
  border-radius: 10px;
  border: 1px solid rgba(230, 197, 148, 0.15);
}

.product-extras-wrapper {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 4px;
  border-left: 2px solid rgba(212, 163, 115, 0.25);
}

.extra-item {
  font-size: 0.95rem;
  color: #bfaea7;
  font-weight: 500;
}

.product-price-col {
  text-align: right;
  flex-shrink: 0;
}

.product-price {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f7ebe6;
}

/* Discount Cards */
.discount-row-card {
  background: rgba(226, 109, 92, 0.04);
  border: 1px dashed rgba(226, 109, 92, 0.3);
  border-radius: 20px;
  padding: 18px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.discount-info-col {
  display: flex;
  align-items: center;
  gap: 14px;
}

.discount-label-badge {
  background: #e26d5c;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 10px;
  letter-spacing: 0.5px;
}

.discount-desc {
  font-size: 1.2rem;
  font-weight: 600;
  color: #f7ebe6;
}

.discount-price-col {
  text-align: right;
}

.discount-amount {
  font-size: 1.45rem;
  font-weight: 700;
  color: #e26d5c;
}

/* Right Panel: Summary */
.summary-section {
  flex: 4;
  background: linear-gradient(180deg, #150e0c 0%, #0c0807 100%);
  border-left: 1px solid rgba(212, 163, 115, 0.15);
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.15);
}

.brand-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
  text-align: center;
}

.brand-logo-small {
  width: 100px;
  height: 100px;
  border-radius: 20px;
  border: 3px solid #e6c594;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.brand-text {
  font-size: 1.4rem;
  font-weight: 800;
  color: #e6c594;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.summary-card {
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 28px;
  padding: 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: inset 0 1px 2px rgba(255, 255, 255, 0.03);
}

.summary-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #f7ebe6;
  margin-bottom: 24px;
  text-align: center;
  letter-spacing: 0.5px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  font-size: 1.15rem;
  color: #bfaea7;
}

.discount-row .summary-value {
  color: #e26d5c;
  font-weight: 600;
}

.summary-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 20px 0;
}

.total-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.total-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #bfaea7;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.total-price-value {
  font-size: 3.5rem;
  font-weight: 800;
  color: #e6c594;
  letter-spacing: -1.5px;
  line-height: 1;
  text-shadow: 0 4px 12px rgba(230, 197, 148, 0.15);
}

.thank-you-message {
  font-size: 1.4rem;
  font-weight: 700;
  color: #e6c594;
  margin-top: 20px;
  letter-spacing: 2px;
  text-transform: uppercase;
  opacity: 0.85;
}

/* Animations */
@keyframes pulseGlow {
  0% {
    box-shadow: 0 0 0 0 rgba(230, 197, 148, 0.4);
    border-color: rgba(230, 197, 148, 0.6);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(230, 197, 148, 0);
    border-color: rgba(230, 197, 148, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(230, 197, 148, 0);
    border-color: rgba(255, 255, 255, 0.02);
  }
}

.new-item-added {
  animation: pulseGlow 0.7s cubic-bezier(0.16, 1, 0.3, 1);
}

/* List item transition effects */
.list-enter-active, 
.list-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.list-enter {
  opacity: 0;
  transform: translateY(20px);
}
.list-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Responsive queries */
@media (max-width: 900px) {
  .cart-screen-layout {
    flex-direction: column;
  }
  .products-section {
    flex: 1;
    padding: 24px;
    height: 60vh;
  }
  .summary-section {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(212, 163, 115, 0.15);
    padding: 28px 24px;
    height: auto;
    gap: 20px;
  }
  .summary-card {
    padding: 20px;
  }
  .total-price-value {
    font-size: 2.8rem;
  }
}
</style>

<style>
/* Global stil - scoped değil */
* {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* IE and Edge */
}

*::-webkit-scrollbar {
  display: none !important; /* Chrome, Safari and Opera */
  width: 0 !important;
}

html, body {
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
}
</style>
